import { useAuthStatus } from '../context/AuthContext';
import { loadingSpinner, getCachedLogoUrl } from '../utils/bookingUtils';
import ConnectionErrorScreen from './ConnectionErrorScreen';

/**
 * Holds a screen behind a full-page spinner until the visitor's anonymous
 * sign-in (started by AuthProvider) has finished, because the Realtime
 * Database rules gate every read on `auth != null`.
 *
 * Wraps only the routes that read the database the moment they mount (admin,
 * POS, checkout, the standalone /bookings page). The guest landing route is
 * deliberately NOT wrapped: it renders from the config snapshot straight
 * away and waits on useAuthStatus().ready only where it touches the database.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - rendered once signed in.
 * @returns {JSX.Element}
 */
export function AuthGuard({ children }) {
  const { ready, failed } = useAuthStatus();

  if (failed) {
    // Nothing behind the guard can render without sign-in, so the error
    // modal floats over the spinner the visitor was already watching. Its
    // button hard-reloads the page, which restarts sign-in from scratch.
    return (
      <>
        {loadingSpinner('Preparing your experience', getCachedLogoUrl())}
        <ConnectionErrorScreen />
      </>
    );
  }

  if (!ready) {
    // Logo comes from the localStorage cache — config may not be here yet.
    return loadingSpinner('Preparing your experience', getCachedLogoUrl());
  }

  return children;
}
